import React, { useContext, useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

import { withViewport } from '../../../util/uiHelpers';
import { FormattedMessage, injectIntl, useIntl } from '../../../util/reactIntl';
import { isScrollingDisabled } from '../../../ducks/ui.duck';

import { LayoutSingleColumn, Page, IconCollection } from '../../../components';
import Container from '../../../components/Layout/Container';

import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import FooterContainer from '../../FooterContainer/FooterContainer';
import MainSearchContent from './MainSearchContent/MainSearchContent';

import css from './AlgoliaSearchPage.module.css';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { useConfiguration } from '../../../context/configurationContext';
import { setActiveListing } from './AlgoliaSearchPage.duck';
import MobileBottomNavbar from '../../../components/MobileBottomNavbar/MobileBottomNavbar';
import { LanguageContext } from '../../../context/LanguageContext';
import { LOCAL_STORAGE_MAP_KEY } from './MainSearchContent/MainSearchContent';
import {
  buildSearchCanonical,
  shouldIndexSearchPage,
  buildSearchTitle,
  buildSearchDescription,
  createSearchResultSchema,
} from '../../../util/seo';
import { loadRentalDefaultDateRange } from '../../../util/rentalDefaults';

function AlgoliaSearchPageComponent(props) {
  const { scrollingDisabled } = props;
  const [scrolling, setScrolling] = useState(false);

  // Dates are stored in React state (NOT in the URL) so that changing them never
  // triggers loadData / a new Algolia query / scroll-to-top.
  const [dateRange, setDateRange] = useState(() => {
    const searchParamsForDates = new URLSearchParams(props.location?.search || '');
    const urlStart = searchParamsForDates.get('startDate') || null;
    const urlEnd = searchParamsForDates.get('endDate') || null;
    if (urlStart && urlEnd) return { startDate: urlStart, endDate: urlEnd };
    return { startDate: null, endDate: null };
  });

  // Prefill from the last date range applied anywhere (search page, store page, or the
  // listing page's own calendar) - see util/rentalDefaults.js. Booking is not required;
  // any genuine Apply becomes the new default (see BookingDatesForm.js/DesktopSearchBar.js/
  // StoreProfilePage.js's own save calls). This single localStorage-backed value also
  // covers what a per-tab sessionStorage cache used to handle here (restoring dates after
  // a listing-detail visit and back navigation) - since every Apply now saves immediately,
  // there's no separate signal left for that cache to carry that this doesn't already.
  // Deliberately an effect rather than part of the useState initializer above, so the
  // localStorage read only ever happens on the client and can't diverge from SSR markup.
  // Runs once on mount; a date range already in the URL is more specific and wins.
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) return;
    const remembered = loadRentalDefaultDateRange();
    if (!remembered) return;
    setDateRange({
      startDate: moment(remembered.startDate).format('YYYY-MM-DD'),
      endDate: moment(remembered.endDate).format('YYYY-MM-DD'),
    });
  }, []);

  // Initialize showMap from localStorage preference (same logic as MainSearchContent)
  const defaultMapPreference =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(LOCAL_STORAGE_MAP_KEY)
      : null;
  const preference = defaultMapPreference
    ? JSON.parse(defaultMapPreference).open
    : false;
  const [showMap, setShowMap] = useState(preference);

  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  // Detect mobile breakpoint
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;

  // Derive search intent from URL params — SSR-safe, no window references
  const searchParams = new URLSearchParams(props.location?.search || '');
  const addressParam = searchParams.get('address') || '';
  const categoryParam = searchParams.get('category') || '';

  // Dead URLs / removed listings redirect here with ?notice=unavailable
  // (see NotFoundPage.js). Read it once on mount so the notice survives the
  // history.replace below that strips the flag from the URL - a refresh or
  // shared link shouldn't keep showing the banner.
  const [showUnavailableNotice] = useState(
    () => new URLSearchParams(props.location?.search || '').get('notice') === 'unavailable'
  );
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  useEffect(() => {
    if (!showUnavailableNotice) return;
    const params = new URLSearchParams(props.location?.search || '');
    if (!params.has('notice')) return;
    params.delete('notice');
    const nextSearch = params.toString();
    props.history.replace({
      pathname: props.location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
    // Strip on mount only - not meant to re-run as filters change.
  }, []);

  const rootUrl = props.config?.marketplaceRootURL || '';
  const marketplaceName = props.config?.marketplaceName || '';

  const canonicalUrl = buildSearchCanonical({
    locationSearch: props.location?.search || '',
    rootUrl,
  });

  const shouldIndex = shouldIndexSearchPage({
    locationSearch: props.location?.search || '',
  });

  const filterParams = {
    address: addressParam,
    category: categoryParam,
  };

  const pageTitle = buildSearchTitle({ filterParams, marketplaceName });
  const pageDescription = buildSearchDescription({ filterParams, marketplaceName });

  const searchSchema = createSearchResultSchema({
    title: pageTitle,
    description: pageDescription,
    canonicalUrl,
    listings: props.algoliaListings || [],
    rootUrl,
  });

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      canonicalUrl={canonicalUrl}
      robots={shouldIndex ? 'index,follow' : 'noindex,follow'}
      schema={searchSchema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer notShowDesktopSearchBar={true} />}
        footer={!(isMobile && showMap) ? <FooterContainer /> : null}
        mobileBottomNavbar={<MobileBottomNavbar setScrolling={setScrolling} />}
      >
        {showUnavailableNotice && !noticeDismissed ? (
          <Container className={css.unavailableNoticeContainer}>
            <div className={css.unavailableNotice} role="status">
              <IconCollection name="info_icon" />
              <span className={css.unavailableNoticeText}>
                <FormattedMessage id="SearchPage.unavailableNotice" />
              </span>
              <button
                type="button"
                className={css.unavailableNoticeClose}
                onClick={() => setNoticeDismissed(true)}
                aria-label={intl.formatMessage({ id: 'SearchPage.unavailableNoticeDismiss' })}
              >
                <IconCollection name="close_grey" />
              </button>
            </div>
          </Container>
        ) : null}
        <MainSearchContent
          {...props}
          scrolling={scrolling}
          locale={locale}
          onShowMapChange={setShowMap}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      </LayoutSingleColumn>
    </Page>
  );
}

const mapStateToProps = state => {
  const {
    activeListingId,
    facets,
    facetsInProgress,
    facetsError,
    algoliaQueryInProgress,
    algoliaBackgroundQueryInProgress,
    algoliaQueryError,
    algoliaListings,
    algoliaStores,
    nbHitsInsideBounds,
    defaultSort
  } = state.AlgoliaSearchPage;
  const {
    currentUserLocationInfo,
    currentUserCurrency,
    currentUser,
  } = state.user;
  const { exchangeRates } = state.exchangeRate;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUserLocationInfo,
    currentUserCurrency,
    exchangeRates,
    currentUser,
    activeListingId,
    facets,
    facetsInProgress,
    facetsError,
    algoliaQueryInProgress,
    algoliaBackgroundQueryInProgress,
    algoliaQueryError,
    algoliaListings,
    algoliaStores,
    nbHitsInsideBounds,
    defaultSort
  };
};

const mapDispatchToProps = dispatch => ({
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

const EnhansedSearchPage = props => {
  const routesConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  return (
    <AlgoliaSearchPageComponent
      routesConfiguration={routesConfiguration}
      config={config}
      {...props}
    />
  );
};

const AlgoliaSearchPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl,
  withRouter
)(EnhansedSearchPage);

export default AlgoliaSearchPage;
